import { createSignal, createEffect, onMount } from 'solid-js';

export type Theme = 'light' | 'dark' | 'system';

// Get initial theme from localStorage or default to system
function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'system';

  const stored = localStorage.getItem('theme') as Theme | null;
  if (stored && ['light', 'dark', 'system'].includes(stored)) {
    return stored;
  }
  return 'system';
}

// Check if system prefers dark mode
function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

// Theme signals
const [theme, setThemeInternal] = createSignal<Theme>(getInitialTheme());
const [resolvedTheme, setResolvedTheme] = createSignal<'light' | 'dark'>(
  getInitialTheme() === 'system' ? getSystemTheme() : getInitialTheme() as 'light' | 'dark'
);

// Apply theme to document
function applyTheme(resolved: 'light' | 'dark') {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(resolved);

  // Update meta theme-color for mobile browsers
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', resolved === 'dark' ? '#09090b' : '#fafafa');
  }
}

// Set theme and persist to localStorage
function setTheme(newTheme: Theme) {
  setThemeInternal(newTheme);
  localStorage.setItem('theme', newTheme);

  const resolved = newTheme === 'system' ? getSystemTheme() : newTheme;
  setResolvedTheme(resolved);
  applyTheme(resolved);
}

// Toggle between light and dark
function toggleTheme() {
  const current = resolvedTheme();
  setTheme(current === 'light' ? 'dark' : 'light');
}

// Initialize theme on mount
function initializeTheme() {
  const initial = theme();
  const resolved = initial === 'system' ? getSystemTheme() : initial;
  setResolvedTheme(resolved);
  applyTheme(resolved);

  // Listen for system theme changes
  if (typeof window !== 'undefined') {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', (e) => {
      if (theme() === 'system') {
        const newResolved = e.matches ? 'dark' : 'light';
        setResolvedTheme(newResolved);
        applyTheme(newResolved);
      }
    });
  }
}

// Export theme store
export const themeStore = {
  theme,
  resolvedTheme,
  setTheme,
  toggleTheme,
  initializeTheme,
  isDark: () => resolvedTheme() === 'dark',
  isLight: () => resolvedTheme() === 'light',
};
